import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, OnDestroy, Output } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { BsModalRef, ModalModule } from 'ngx-bootstrap/modal';
import { ToastrModule, ToastrService } from 'ngx-toastr';
import { BreadcrumbComponent } from '../../reusable/breadcrumb/breadcrumb.component';
import { MarkdownEditorComponent } from '../../reusable/markdown-editor/markdown-editor.component';
import { ApiService } from '../../../services/api.service';
import { SingleSelectDropdownComponent } from '../../reusable/single-select-dropdown/single-select-dropdown.component';

@Component({
  selector: 'app-create-research-studies',
  imports: [ModalModule, CommonModule, FormsModule, ReactiveFormsModule, ToastrModule, BreadcrumbComponent, MarkdownEditorComponent, SingleSelectDropdownComponent],
  templateUrl: './create-research-studies.component.html',
  styleUrl: './create-research-studies.component.scss'
})
export class CreateResearchStudiesComponent implements OnDestroy {
  @Output() modalClosed = new EventEmitter<void>();
  private readonly allowedDocumentExtensions = ['xlsx', 'xls'];
  private readonly allowedDocumentMimeTypes = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel'
  ];
  researchSudiesData: any;
  parentPage: any
  researchStudiesForm!: FormGroup;
  breadcrumbItems: Array<{ label: string }> = [];
  eligibility: any[] = [
    {id: 1, name: "Adults aged 18–65 years with BMI ≥ 30 kg/m²"},
    {id: 2, name: "Adults diagnosed with primary hypertension"},
    {id: 3, name: "Adults with Type 2 Diabetes (HbA1c 7–10%)"},
    
  ]
  globalTagList: any[] = [];
  agGrid: any;
  uploadedDocuments: Array<{ file: File; url: string }> = [];
  showUploadDocumentField = true;
  whoCanJoinList: string[] = [];

  constructor(
    public modalRef: BsModalRef,
    private fb: FormBuilder,
    private apiService: ApiService,
    private cdr: ChangeDetectorRef,
    private toastr: ToastrService
  ) { }

  ngOnInit() {
    this.researchStudiesForm = this.fb.group({
      study_name: [null, [Validators.required, Validators.maxLength(100)]],
      by_whom: [null, [Validators.required]],
      registry_title: [null, [Validators.required]],
      who_can_join: [[], [Validators.required]],
      duration_of_study: [null, [Validators.required]],
      duration_unit: ['month', [Validators.required]],
      completion_config: [null],
      global_tag_id: [null, [Validators.required]],
      // description: [null, [Validators.required]],
      deleted: [false]
    });

    if (this.researchSudiesData) {
      const whoCanJoinRaw = this.researchSudiesData.who_can_join;
      const whoCanJoinArray = Array.isArray(whoCanJoinRaw)
        ? whoCanJoinRaw
        : typeof whoCanJoinRaw === 'string'
          ? whoCanJoinRaw.split(',').map((item: string) => item.trim()).filter((item: string) => !!item)
          : [];

      this.whoCanJoinList = [...whoCanJoinArray];

      const globalTagIdRaw = this.researchSudiesData.global_tag_id;
      const normalizedGlobalTagId = Array.isArray(globalTagIdRaw)
        ? (globalTagIdRaw[0] ?? null)
        : globalTagIdRaw;

      this.researchStudiesForm.patchValue({
        ...this.researchSudiesData,
        who_can_join: whoCanJoinArray,
        global_tag_id: normalizedGlobalTagId,
      });
      if (typeof this.researchSudiesData.duration_of_study === 'string') {
        const durationValue = this.researchSudiesData.duration_of_study.trim();
        const durationMatch = durationValue.match(/^(\d+)\s*(month|months|year|years)?$/i);
        if (durationMatch) {
          const parsedUnit = durationMatch[2]?.toLowerCase().includes('year') ? 'year' : 'month';
          this.researchStudiesForm.patchValue({
            duration_of_study: durationMatch[1],
            duration_unit: parsedUnit,
          });
        }
      }
      this.syncGlobalTagSelection();
    }
    this.getGlobalTags();
    this.breadcrumbItems = [
      { label: this.parentPage },
      { label: 'Create Research Studies' }
    ];
  }

   onSubmit() {
    if (this.researchStudiesForm.valid) {
      const formValue = this.researchStudiesForm.value;
      const { duration_unit, ...restFormValue } = formValue;
      const payload = {
        ...restFormValue,
        duration_of_study: restFormValue.duration_of_study
          ? `${restFormValue.duration_of_study} ${duration_unit}`.trim()
          : restFormValue.duration_of_study,
        who_can_join: Array.isArray(restFormValue.who_can_join)
          ? restFormValue.who_can_join.join(', ')
          : restFormValue.who_can_join,
        global_tag_id: Array.isArray(restFormValue.global_tag_id)
          ? (restFormValue.global_tag_id[0] ?? null)
          : restFormValue.global_tag_id,
      };
      console.log(payload);
      const formData = new FormData();
      formData.append('payload', JSON.stringify(payload));
      if (this.uploadedDocuments.length > 0) {
        formData.append('completion_config_file', this.uploadedDocuments[0].file);
      }

      if (this.researchSudiesData) {
        this.apiService.patch(`research-studies/update/${this.researchSudiesData.id}`, formData).subscribe({
          next: (res: any) => {
            this.toastr.success('Research study updated successfully!', 'Success');
            this.closeModal();
            this.researchStudiesForm.patchValue(res);
          },
          error: (err) => {
            console.error('Error updating research study:', err);
            this.toastr.error(err.error?.message || 'Failed to update research study.', 'Error');
          }
        });
      } else {
        this.apiService.post('research-studies', formData).subscribe({
          next: (res: any) => {
            this.toastr.success('Research study created successfully!', 'Success');
            this.closeModal();
          },
          error: (err) => {
            console.error('Error creating research study:', err);
            this.toastr.error(err.error?.message || 'Failed to create research study.', 'Error');
          }
        });
      }
    } else {
      this.researchStudiesForm.markAllAsTouched();
    }
  }

  getGlobalTags(): void {
    this.apiService.get('globalTag').subscribe({
      next: (res: any) => {
        this.globalTagList = Array.isArray(res?.data) ? [...res.data] : [];
        this.syncGlobalTagSelection();
        if (this.agGrid) {
          this.agGrid.api.refreshCells({ force: true });
        }
      },
      error: (err) => {
        console.log(err);
      }
    })
  }

  private syncGlobalTagSelection(): void {
    if (!this.researchSudiesData || !this.researchStudiesForm) return;
    const control = this.researchStudiesForm.get('global_tag_id');
    if (!control) return;

    const globalTagIdRaw = this.researchSudiesData.global_tag_id;
    const normalizedGlobalTagId = Array.isArray(globalTagIdRaw)
      ? (globalTagIdRaw[0] ?? null)
      : globalTagIdRaw;

    // Force CVA re-write after options load so selected label appears without user click.
    control.setValue(null, { emitEvent: false });
    setTimeout(() => {
      control.setValue(normalizedGlobalTagId, { emitEvent: false });
      this.cdr.detectChanges();
    });
  }

  onDocumentsSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;

    if (!files?.length) {
      return;
    }

    const selectedFile = files[0];
    if (!this.isAllowedDocument(selectedFile)) {
      this.toastr.error('Only .xlsx and .xls files are allowed.', 'Invalid file type');
      input.value = '';
      return;
    }

    this.uploadedDocuments.forEach((document) => URL.revokeObjectURL(document.url));
    this.uploadedDocuments = [{
      file: selectedFile,
      url: URL.createObjectURL(selectedFile)
    }];
    this.showUploadDocumentField = false;
    this.researchStudiesForm.patchValue({ completion_config: null });
    input.value = '';
    this.cdr.detectChanges();
  }

  private isAllowedDocument(file: File): boolean {
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    const isAllowedExtension = this.allowedDocumentExtensions.includes(extension);
    const isAllowedMimeType = this.allowedDocumentMimeTypes.includes(file.type);

    return isAllowedExtension || isAllowedMimeType;
  }

  showUploadField(): void {
    this.showUploadDocumentField = true;
    this.cdr.detectChanges();
  }

  viewDocument(document: { file: File; url: string }): void {
    window.open(document.url, '_blank', 'noopener,noreferrer');
  }

  downloadDocument(document: { file: File; url: string }): void {
    const link = window.document.createElement('a');
    link.href = document.url;
    link.download = document.file.name;
    link.click();
  }

  removeDocument(index: number): void {
    const [document] = this.uploadedDocuments.splice(index, 1);
    if (document) {
      URL.revokeObjectURL(document.url);
    }

    this.showUploadDocumentField = true;
    this.researchStudiesForm.patchValue({ completion_config: null });
    this.researchStudiesForm.get('completion_config')?.markAsTouched();
    this.cdr.detectChanges();
  }

  formatFileSize(size: number): string {
    if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(1)} KB`;
    }

    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }

  get completionConfigViewerData(): any {
    const completionConfigValue = this.researchStudiesForm?.get('completion_config')?.value;
    if (completionConfigValue) {
      return this.parseCompletionConfig(completionConfigValue);
    }

    if (this.uploadedDocuments.length > 0) {
      return null;
    }

    return this.parseCompletionConfig(this.researchSudiesData?.completion_config);
  }

  get showCompletionConfigViewer(): boolean {
    return !!this.researchSudiesData && this.uploadedDocuments.length === 0 && !!this.completionConfigViewerData;
  }

  private parseCompletionConfig(value: any): any {
    if (!value) return null;
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    }
    return value;
  }

  ngOnDestroy(): void {
    this.uploadedDocuments.forEach((document) => URL.revokeObjectURL(document.url));
  }

closeModal() {
    this.uploadedDocuments.forEach((document) => URL.revokeObjectURL(document.url));
    this.uploadedDocuments = [];
    this.showUploadDocumentField = true;
    this.modalClosed.emit();
    this.modalRef.hide();
    this.cdr.detectChanges();
  }

  
private updateWhoCanJoinControl(): void {
  this.researchStudiesForm.patchValue({
    who_can_join: this.whoCanJoinList
  });
}

private pushWhoCanJoinValue(rawValue: string): void {
  const value = rawValue?.trim();
  if (!value || this.whoCanJoinList.includes(value)) return;
  this.whoCanJoinList.push(value);
  this.updateWhoCanJoinControl();
}

addChip(event: any): void {
  event.preventDefault();
  const input = event.target as HTMLInputElement;
  this.pushWhoCanJoinValue(input.value);
  input.value = '';
}

addChipOnBlur(event: any): void {
  const input = event.target as HTMLInputElement;
  this.pushWhoCanJoinValue(input.value);
  input.value = '';
}

removeChip(index: number): void {
  this.whoCanJoinList.splice(index, 1);
  this.updateWhoCanJoinControl();
}
}
